import { useState } from 'react';
import { CAP_STATUSES, CAP_TYPES } from '@siliconvault/shared';
import type { CapacitorEntry } from '@siliconvault/shared';
import { useUpdateCapStatus, useUpdateCapacitorMap, useCreateCapacitorMap } from '../../hooks/useCapacitorMap';
import { Button } from '../ui/Button';
import { Input, Select } from '../ui/Input';
import { useToast } from '../ui/Toast';
import { useTranslation } from '../../i18n';

const STATUS_COLORS: Record<string, string> = {
  original: '#FFB800',
  replaced: '#33FF33',
  leaking: '#FF3333',
  removed: '#888888',
  not_applicable: '#555555',
};

interface CapacitorListProps {
  itemId: string;
  capacitors: CapacitorEntry[];
  progress: { total: number; replaced: number; percent: number };
  hasMap: boolean;
  canEdit: boolean;
}

export function CapacitorList({ itemId, capacitors, progress, hasMap, canEdit }: CapacitorListProps) {
  const updateStatus = useUpdateCapStatus();
  const updateMap = useUpdateCapacitorMap();
  const createMap = useCreateCapacitorMap();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [showAdd, setShowAdd] = useState(false);
  const [newCap, setNewCap] = useState({ designator: '', value: '', voltage: '', type: 'electrolytic' as string });

  async function handleStatusChange(designator: string, status: string) {
    try {
      await updateStatus.mutateAsync({
        itemId,
        designator,
        data: { status, replacedDate: status === 'replaced' ? new Date().toISOString().split('T')[0] : null },
      });
    } catch {
      toast('Failed to update status', 'error');
    }
  }

  async function handleAddCap() {
    if (!newCap.designator) return;
    const entry: CapacitorEntry = {
      designator: newCap.designator,
      value: newCap.value,
      voltage: newCap.voltage,
      type: newCap.type as any,
      status: 'original',
      notes: '',
    };

    try {
      if (hasMap) {
        await updateMap.mutateAsync({
          itemId,
          data: { capacitors: [...capacitors, entry] },
        });
      } else {
        await createMap.mutateAsync({
          itemId,
          data: { capacitors: [entry] },
        });
      }
      toast('Capacitor added', 'success');
      setNewCap({ designator: '', value: '', voltage: '', type: 'electrolytic' });
      setShowAdd(false);
    } catch {
      toast('Failed to add capacitor', 'error');
    }
  }

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <h3 style={{ fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)' }}>
          {t('cap.title')}
        </h3>
        {canEdit && (
          <Button size="small" variant="secondary" onClick={() => setShowAdd(!showAdd)}>
            {showAdd ? t('cap.cancel') : t('cap.addCap')}
          </Button>
        )}
      </div>

      {/* Progress bar */}
      <div style={{
        display: 'flex',
        gap: '0.25rem',
        marginBottom: '0.75rem',
        padding: '0.5rem',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
      }}>
        {progress.total > 0 ? (
          <>
            <div style={{ flex: 1 }}>
              <div style={{
                height: '8px',
                background: 'var(--bg-tertiary)',
                borderRadius: '4px',
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  width: `${progress.percent}%`,
                  background: '#33FF33',
                  borderRadius: '4px',
                  transition: 'width 0.3s',
                }} />
              </div>
            </div>
            <span style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.6875rem',
              color: '#33FF33',
              minWidth: '3rem',
              textAlign: 'right',
            }}>
              {progress.percent}%
            </span>
          </>
        ) : (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
            {t('cap.noCapsMapped')}
          </span>
        )}
      </div>

      {showAdd && (
        <div style={{
          padding: '0.75rem',
          marginBottom: '0.75rem',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 1fr auto',
          gap: '0.5rem',
          alignItems: 'end',
        }}>
          <Input label={t('cap.designator')} value={newCap.designator} onChange={(e) => setNewCap({ ...newCap, designator: e.target.value })} placeholder="C1" />
          <Input label={t('cap.value')} value={newCap.value} onChange={(e) => setNewCap({ ...newCap, value: e.target.value })} placeholder="100uF" />
          <Input label={t('cap.voltage')} value={newCap.voltage} onChange={(e) => setNewCap({ ...newCap, voltage: e.target.value })} placeholder="16V" />
          <Select label={t('cap.type')} value={newCap.type} onChange={(e) => setNewCap({ ...newCap, type: e.target.value })}>
            {CAP_TYPES.map((ct) => <option key={ct} value={ct}>{ct}</option>)}
          </Select>
          <Button size="small" onClick={handleAddCap}>{t('cap.add')}</Button>
        </div>
      )}

      {capacitors.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>{t('cap.designator')}</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>{t('cap.value')}</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>{t('cap.type')}</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>{t('cap.status')}</th>
              </tr>
            </thead>
            <tbody>
              {capacitors.map((cap) => (
                <tr key={cap.designator} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.5rem 0.75rem', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{cap.designator}</td>
                  <td style={{ padding: '0.5rem 0.75rem', fontFamily: 'var(--font-mono)' }}>{cap.value} {cap.voltage}</td>
                  <td style={{ padding: '0.5rem 0.75rem', fontFamily: 'var(--font-mono)' }}>{cap.type}</td>
                  <td style={{ padding: '0.5rem 0.75rem' }}>
                    {canEdit ? (
                      <select
                        value={cap.status}
                        onChange={(e) => handleStatusChange(cap.designator, e.target.value)}
                        style={{
                          background: 'transparent',
                          color: STATUS_COLORS[cap.status] ?? 'var(--text-primary)',
                          border: `1px solid ${STATUS_COLORS[cap.status] ?? 'var(--border)'}`,
                          borderRadius: '4px',
                          padding: '0.125rem 0.375rem',
                          fontSize: '0.6875rem',
                          fontFamily: 'var(--font-mono)',
                          cursor: 'pointer',
                        }}
                      >
                        {CAP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <span style={{ color: STATUS_COLORS[cap.status], fontFamily: 'var(--font-mono)', fontSize: '0.6875rem' }}>
                        {cap.status}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
